import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/shared/lib/cn';

export type ResponseProps = React.HTMLAttributes<HTMLDivElement>;

const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="mb-2 list-disc space-y-1 pl-4 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2 list-decimal space-y-1 pl-4 last:mb-0">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  code: ({ children, className }) => {
    const inline = !className;
    if (inline) {
      return <code className="rounded bg-foreground/10 px-1 py-0.5 font-mono text-[0.85em]">{children}</code>;
    }
    return (
      <code className="block overflow-x-auto rounded-lg bg-foreground/10 p-2 font-mono text-[0.8em]">{children}</code>
    );
  },
  pre: ({ children }) => <pre className="mb-2 overflow-x-auto last:mb-0">{children}</pre>,
  h1: ({ children }) => <p className="mb-2 text-base font-semibold last:mb-0">{children}</p>,
  h2: ({ children }) => <p className="mb-2 text-sm font-semibold last:mb-0">{children}</p>,
  h3: ({ children }) => <p className="mb-1.5 text-sm font-semibold last:mb-0">{children}</p>,
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-primary/40 pl-3 text-muted-foreground last:mb-0">{children}</blockquote>
  ),
  hr: () => <hr className="my-3 border-border/60" />,
};

/**
 * Resposta da IA — markdown (negrito, lista, código). O modelo manda `**texto**`;
 * sem parser isso aparecia literal no chat.
 */
export const Response = React.forwardRef<HTMLDivElement, ResponseProps>(
  ({ className, children, ...props }, ref) => {
    const markdown = typeof children === 'string' ? children : '';
    return (
      <div ref={ref} className={cn('break-words text-sm [&_p:only-child]:mb-0', className)} {...props}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {markdown}
        </ReactMarkdown>
      </div>
    );
  },
);
Response.displayName = 'Response';
